// src/ConsumerApp/Screens/Bucket/Bucket.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import BucketItemCard from './components/BucketItemCard';
import DeliveryAddressModal from '../Addresses/DeliveryAddressModal';
import { useAuthModal } from '../../context/AuthModalContext';
import { useToast } from '../../context/ToastContext';
import { guestBucketManager } from '../../utils/guestBucketManager';

// SVGs
import X from '../../../assets/icons/X.svg';
import SealPercent from '../../../assets/icons/SealPercent.svg';
import WalletIcon from '../../../assets/icons/Wallet.svg';
import Clock from '../../../assets/icons/Clock.svg';
import Truck from '../../../assets/icons/Truck.svg';
import ShieldCheck from '../../../assets/icons/ShieldCheck.svg';
import Lightning from '../../../assets/icons/Lightning.svg';
import ArrowRight from '../../../assets/icons/ArrowRight.svg';
import bucket from '../../../assets/icons/bucketConcrete.svg';
import BrickPointImage from '../../../assets/images/BrickPointImage1.png';

// Shared API Hooks
import {
  useGetBucketItemsAndBillSummary,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
  useUpdateBucketUnloading,
  useUpdateBucketRedeemableBrickPoints,
  useGenerateShareBucketLink
} from '../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../api/savedList/savedList.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';
import {
  useEvaluateDiscounts,
  useApplyDiscount,
  useRemoveDiscount,
} from '../../api/offers/discounts.hooks';
import { useGetWalletBalance, useApplyWalletBalance } from '../../api/wallet/wallet.hooks';
import {
  useInitiatePayment,
  useVerifyPaymentStatus,
  useGetOrderByTransactionId,
} from '../../api/payment/payment.hooks';
import { load } from '@cashfreepayments/cashfree-js';
import { useUpdateCustomerRegistration } from '../../api/profile/profile.hooks';

export default function Bucket({ isOpen, onClose }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { openLoginModal } = useAuthModal();
  // --- Share & Earn State ---
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
 
  const generateShareLinkMutation = useGenerateShareBucketLink();
  const initiatePaymentMutation = useInitiatePayment();
  const verifyPaymentMutation = useVerifyPaymentStatus();
  const getOrderMutation = useGetOrderByTransactionId();
  const [isNavigating, setIsNavigating] = useState(false);

  // 1. Reactive Session State
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const handleAuthChange = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        setCustomer(stored ? JSON.parse(stored) : null);
      } catch {
        setCustomer(null);
      }
    };
    window.addEventListener('auth-state-change', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    return () => {
      window.removeEventListener('auth-state-change', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, []);

  const customerId = customer?._id || customer?.id;
  const isAuthenticated = Boolean(customerId);
  const activeTab = 'BUY_NOW';

  const updateRegistrationMutation = useUpdateCustomerRegistration(customer?.token);
  const [customerName, setCustomerName] = useState(customer?.customer_name || '');

  // 2. Reactive Guest Items
  const [guestItems, setGuestItems] = useState(() => guestBucketManager.getItems());

  useEffect(() => {
    const handleGuestBucket = () => {
      setGuestItems(guestBucketManager.getItems());
    };
    window.addEventListener('guest-bucket-change', handleGuestBucket);
    return () => window.removeEventListener('guest-bucket-change', handleGuestBucket);
  }, []);

  const guestSubtotal = useMemo(() => {
    return guestItems.reduce((acc, item) => {
      const p = Number(item.unit_price || item.price || item.selling_price || 0);
      const q = Number(item.quantity || 1);
      return acc + p * q;
    }, 0);
  }, [guestItems]);

  const [useWallet, setUseWallet] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('ONLINE');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [evaluatedDiscounts, setEvaluatedDiscounts] = useState([]);
  const [isEvaluatingDiscounts, setIsEvaluatingDiscounts] = useState(false);

  // Brick Points Modal State
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const [profType, setProfType] = useState('');
  const [gstin, setGstin] = useState('');

  // 3. Scroll Locking
  useEffect(() => {
    if (isOpen) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      document.body.style.overflow = 'hidden';
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  // 4. Data Fetching
  const { data: rawAddressRes, isLoading: isAddressLoading } = useGetDefaultDeliveryAddress(customerId);
  const updateUnloadingMutation = useUpdateBucketUnloading();
  const updateRedeemableBrickPointsMutation = useUpdateBucketRedeemableBrickPoints();

  const deliveryAddress = useMemo(() => {
    if (!rawAddressRes) return null;
    return rawAddressRes.data?.address || rawAddressRes.data || rawAddressRes.address || rawAddressRes;
  }, [rawAddressRes]);

  const currentAddressId = deliveryAddress?._id || deliveryAddress?.delivery_address_id || null;

  const {
    data: bucketRes,
    refetch: refetchBucketData,
    isLoading: isBucketLoading,
  } = useGetBucketItemsAndBillSummary({
    customer_id: customerId,
    purchase_type: activeTab,
    delivery_address_id: currentAddressId,
  });
  const isLoadingItems = isAuthenticated ? isBucketLoading : false;
  const customer_status = bucketRes?.bucket_data?.customer_status || customer?.customer_status
  const PointsLocked = customer_status === 3 ? false : true
  const recommendationEligible = bucketRes?.bucket_data?.is_recommendation_eligible || false;
  //console.log('bucketRes------',bucketRes)

  const { data: savedList = [] } = useGetSavedList(customerId);
  const addItemToSavedMutation = useAddItemToSavedList();
  const removeItemFromSavedMutation = useRemoveItemFromSavedList();

  const savedItemsSet = useMemo(() => {
    const list = Array.isArray(savedList) ? savedList : savedList?.items || [];
    return new Set(list.map((item) => String(item.product_id || item.item_id || item._id)));
  }, [savedList]);


  const { data: walletData } = useGetWalletBalance(customerId);
  const availableWalletBalance = walletData?.available_wallet_balance || 0;

  const updateItemMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();
  const applyWalletMutation = useApplyWalletBalance();
  const evaluateDiscountsMutation = useEvaluateDiscounts();
  const applyDiscountMutation = useApplyDiscount();
  const removeDiscountMutation = useRemoveDiscount();

  // Unified items extraction across schemas
  const rawBucketData = bucketRes?.bucket_data || bucketRes?.data?.bucket_data || bucketRes?.data || bucketRes || {};
  ///console.log('rawBucketData', rawBucketData)
  const serverItems = useMemo(() => {
    if (Array.isArray(rawBucketData?.items)) return rawBucketData.items;
    if (Array.isArray(rawBucketData?.buy_now_bucket?.items)) return rawBucketData.buy_now_bucket.items;
    if (Array.isArray(bucketRes?.items)) return bucketRes.items;
    return [];
  }, [rawBucketData, bucketRes]);

  const brickPointsEarned = rawBucketData?.brick_points_earned || 0;
  const brickPointsEligible = true;

  const displayItems = isAuthenticated ? serverItems : guestItems;

  const mfcItems = displayItems.filter((item) => !item.isSupplierDelivered);
  const supplierItems = displayItems.filter((item) => item.isSupplierDelivered);

  // Out of Stock detection
  const hasOutOfStockItems = useMemo(() => {
    return displayItems.some(
      (item) =>
        item.out_of_stock === true ||
        item.is_out_of_stock === true ||
        item.inventory_status?.toLowerCase() === 'out_of_stock' ||
        item.stock === 0
    );
  }, [displayItems]);

  const billSummary = useMemo(() => {
    return rawBucketData?.bill_summary || rawBucketData?.buy_now_bucket?.bill_summary || {};
  }, [rawBucketData]);

  const deliveryOptions = Array.isArray(rawBucketData?.delivery_options) ? rawBucketData.delivery_options : [];
  const deliveryServiceable = rawBucketData?.delivery_serviceable !== false;
  const deliveryFailureMessage = rawBucketData?.delivery_failure_message || 'Delivery is currently not available for this site.';
  const deliveryFailureReason = rawBucketData?.delivery_failure_reason || null;
  const serverSelectedDeliveryType = rawBucketData?.selected_delivery_type || rawBucketData?.selected_delivery_option?.type || 'FAST';
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(serverSelectedDeliveryType);

  useEffect(() => {
    if (rawBucketData?.selected_delivery_type || rawBucketData?.selected_delivery_option?.type) {
      setSelectedDeliveryType(serverSelectedDeliveryType);
    }
  }, [rawBucketData, serverSelectedDeliveryType]);

  const selectedDeliveryOption =
    deliveryOptions.find((o) => o.type === selectedDeliveryType) ||
    rawBucketData?.selected_delivery_option ||
    deliveryOptions[0];

  const serverDeliveryCharge = Number(billSummary?.delivery_charge) || 0;
  const selectedDeliveryCharge = Number(selectedDeliveryOption?.deliveryCharge) || 0;
  const totalPayableAmount = Number(billSummary?.total_payable) || 0;
  const selectedTotalPayable = totalPayableAmount;

  const isBulkEligible = (isAuthenticated ? totalPayableAmount : guestSubtotal) >= 50000;
  const isOverLimit = (isAuthenticated ? totalPayableAmount : guestSubtotal) >= 100000;

  const addressDisplayName = useMemo(() => {
    if (deliveryAddress?.address_type === 'project' && deliveryAddress.project_name) {
      return `Deliver to: ${deliveryAddress.project_name}`;
    }
    return 'Deliver to: My Site Address';
  }, [deliveryAddress]);

  const formattedAddress = useMemo(() => {
    if (!deliveryAddress) return 'No delivery address selected. Click change to set one.';
    const parts = [
      deliveryAddress.flat_number,
      deliveryAddress.building_name,
      deliveryAddress.area_sector_locality || deliveryAddress.complete_address,
      deliveryAddress.city,
      deliveryAddress.pincode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Address available on file';
  }, [deliveryAddress]);

  useEffect(() => {
    setUseWallet(Boolean(billSummary?.wallet_balance > 0));
  }, [billSummary?.wallet_balance]);

  const handleUpdateQty = (itemId, newQty) => {
    if (isAuthenticated) {
      updateItemMutation.mutate({
        customer_id: customerId,
        item_id: itemId,
        purchase_type: activeTab,
        quantity: newQty,
      });
    } else {
      if (newQty <= 0) {
        guestBucketManager.removeItem(itemId);
      } else {
        guestBucketManager.updateQuantity(itemId, newQty);
      }
    }
  };

  const handleRemoveItem = (itemId) => {
    if (isAuthenticated) {
      deleteItemMutation.mutate(
        { customer_id: customerId, item_id: itemId, bucket_id: rawBucketData?.bucket_id },
        {
          onSuccess: () => {
            refetchBucketData();
            queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerId] });
          },
        }
      );
    } else {
      guestBucketManager.removeItem(itemId);
    }
  };

  // --- Share Handlers ---
  const handleShareAndEarn = async () => {
    if (!customerId || !rawBucketData?.bucket_id) {
      alert("Missing required order information.");
      return;
    }

    setIsGeneratingLink(true);
    try {
      const res = await generateShareLinkMutation.mutateAsync({
        customer_id: customerId,
        bucket_id: rawBucketData?.bucket_id,
        suggestion_type: "BUCKET_SHARE",
      });

      if (res?.success && res?.share_url) {
        setShareUrl(res.share_url);
        setIsShareModalOpen(true);
      } else {
        alert("Could not generate share link.");
      }
    } catch (err) {
      console.error("Link generation error:", err);
      alert("Failed to connect to the server.");
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const shareMessage = `Check out this bucket order! Use this link to view and complete the payment: ${shareUrl}`;

  const copyToClipboard = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareMessage);
      alert("Link copied to clipboard successfully!");
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareMessage;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("Copy");
      textArea.remove();
      alert("Link copied to clipboard successfully!");
    }
  };

  const shareViaWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`, '_blank');
  };

  const shareViaTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent("Check out this bucket order!")}`, '_blank');
  };

  const shareViaWebAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Share Bucket',
          text: 'Check out this bucket order! Use this link to view and complete the payment:',
          url: shareUrl,
        });
      } catch (err) {
        console.error("Native share cancelled or failed:", err);
      }
    } else {
      copyToClipboard();
    }
  };

  const handleToggleSave = async (item, shouldSave) => {
    if (!customerId) {
      onClose();
      openLoginModal();
      return false;
    }
    const pid = String(item.item_id || item.product_id || item._id || item.id);
    try {
      if (shouldSave) {
        await addItemToSavedMutation.mutateAsync({ customer_id: customerId, product_id: pid });
        showToast('Item saved to your saved list.', 'success');
      } else {
        await removeItemFromSavedMutation.mutateAsync({ customer_id: customerId, product_id: pid });
        showToast('Item removed from saved list.', 'info');
      }
      queryClient.invalidateQueries({ queryKey: ['savedList', customerId] });
      return true;
    } catch (err) {
      console.error('Failed to toggle saved item:', err);
      showToast('Could not update saved list. Please try again.', 'error');
      return false;
    }
  };

  const handleWalletToggle = async (e) => {
    const nextVal = e.target.checked;
    if (nextVal && (!availableWalletBalance || availableWalletBalance <= 0)) {
      alert('You do not have any wallet balance available to apply.');
      return;
    }
    setUseWallet(nextVal);
    try {
      await applyWalletMutation.mutateAsync({
        customer_id: customerId,
        bucket_id: rawBucketData?.bucket_id,
        apply_wallet: nextVal,
      });
      refetchBucketData();
    } catch {
      setUseWallet(!nextVal);
    }
  };

  const handleOpenDiscounts = async () => {
    setIsDiscountModalOpen(true);
    setIsEvaluatingDiscounts(true);
    try {
      const payload = {
        event: { event: 'order' },
        consumer: { customer_id: customerId, customer_type: customer?.customer_type },
        context: {
          brand: [...new Set(displayItems.map((i) => i.brand).filter(Boolean))],
          category: [...new Set(displayItems.map((i) => i.category).filter(Boolean))],
          items: displayItems.map((i) => ({ item_id: i.item_id || i.product_id, qty: i.quantity, price: i.price })),
          amount: billSummary?.item_total?.toFixed(2) || 0,
          partner_id: customer?.hubId || 'default',
        },
      };
      const res = await evaluateDiscountsMutation.mutateAsync(payload);
      setEvaluatedDiscounts(res?.data || res || []);
    } catch (err) {
      console.error('Coupon evaluation failed', err);
    } finally {
      setIsEvaluatingDiscounts(false);
    }
  };

  const handleApplyDiscount = async (discount) => {
    try {
      await applyDiscountMutation.mutateAsync({
        consumer: { customer_id: customerId },
        order: { order_id: rawBucketData?.bucket_id },
        support_requested: discount.discount_amount || 0,
        metadata: { code: discount.code, title: discount.title },
      });
      refetchBucketData();
      setIsDiscountModalOpen(false);
    } catch {
      alert('Could not apply this offer.');
    }
  };

  const handleRemoveDiscount = async (ruleCode) => {
    try {
      await removeDiscountMutation.mutateAsync({
        consumer: { customer_id: customerId },
        order: { order_id: rawBucketData?.bucket_id },
        metadata: { code: ruleCode },
      });
      refetchBucketData();
    } catch {
      alert('Could not remove this offer.');
    }
  };

  const GetPanDetailsByGstNumber = async (gstinVal) => {
    if (!gstinVal || typeof gstinVal !== 'string') return null;
    const cleanGstin = gstinVal.trim().toUpperCase();
    if (cleanGstin.length !== 15) return null;
    const extractedPan = cleanGstin.slice(2, 12);
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(extractedPan)) {
      return null;
    }
    return extractedPan;
  };

  const handleVerifyPointsProfile = async () => {
    if (!profType) {
      alert('Please select your Customer Type to continue.');
      return;
    }

    try {
      let extractedPan = '';
      if (gstin) {
        extractedPan = await GetPanDetailsByGstNumber(gstin);
        if (!extractedPan) {
          alert('Invalid GSTIN format. Please check the 15-digit number and try again.');
          return;
        }
      }

      await updateRegistrationMutation.mutateAsync({
        _id: customerId,
        customer_type: profType,
        customer_name: customerName,
        gstin: gstin.toUpperCase(),
        pan: extractedPan,
      });

      alert('Verification details submitted successfully! Your Brick Points will be unlocked upon admin approval.');
      setIsPointsModalOpen(false);
      refetchBucketData();
    } catch (err) {
      console.error('Verification error', err);
      alert(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    }
  };

  const verifyBackendPaymentStatus = async (orderId) => {
    setIsProcessingPayment(true);
    try {
      const result = await verifyPaymentMutation.mutateAsync({
        order_id: orderId,
        customer_id: customerId,
      });
      const status = (result?.status || result?.data?.status || '').toUpperCase();

      if (['SUCCESS', 'COMPLETED', 'PAID'].includes(status)) {
        setPaymentStatus({
          type: 'success',
          navOrderId: orderId,
          message: 'Your payment was successful. Your order is confirmed!',
        });
        queryClient.setQueryData(['bucketSummary', customerId], (prev) => ({
          ...prev,
          buy_now_bucket: {
            ...(prev?.buy_now_bucket || {}),
            items_count: 0,
            items: [],
            bill_summary: { total_payable: 0, sub_total: 0 },
          },
        }));
        queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerId] });
        queryClient.invalidateQueries({ queryKey: ['bucketDetails', customerId] });
        queryClient.invalidateQueries({ queryKey: ['customerOrders', customerId] });
        refetchBucketData();
      } else if (['PENDING', 'PROCESSING', 'IN_PROGRESS'].includes(status)) {
        setPaymentStatus({
          type: 'pending',
          navOrderId: orderId,
          message: 'Your payment is being processed by your bank. We will update you once confirmed.',
        });
      } else if (['CANCELLED', 'USER_DROPPED', 'ABORTED'].includes(status)) {
        setPaymentStatus({
          type: 'failed',
          message: 'Payment was cancelled. No money was deducted.',
        });
      } else {
        setPaymentStatus({
          type: 'failed',
          message: result?.message || result?.data?.message || 'Payment transaction failed. Please try again.',
        });
      }
    } catch {
      setPaymentStatus({
        type: 'pending',
        navOrderId: orderId,
        message: 'Unable to verify payment instantly. Please check your order status shortly.',
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleUnloadingToggle = async (e) => {
    const isApplied = e.target.checked;
    try {
      await updateUnloadingMutation.mutateAsync({
        customer_id: customerId,
        bucket_id: rawBucketData?.bucket_id,
        is_unloading_applied: isApplied,
      });
      refetchBucketData();
    } catch (error) {
      console.error('Failed to update unloading preference:', error);
      alert('Could not update unloading service. Please try again.');
    }
  };

  const handleBrickPointsToggle = async (e) => {
    const isApplied = e.target.checked;
    try {
      await updateRedeemableBrickPointsMutation.mutateAsync({
        customer_id: customerId,
        bucket_id: rawBucketData?.bucket_id,
        is_redeemable_brick_points_applied: isApplied,
      });  
      // Refetch bucket to sync the updated total and flags
      refetchBucketData();
    } catch (error) {
      console.error('Failed to update brick points preference:', error);
      alert('Could not update Brick Points. Please try again.');
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      onClose();
      openLoginModal();
      return;
    }

    if (hasOutOfStockItems) {
      alert('Please remove out of stock items from your bucket before proceeding.');
      return;
    }

    if (selectedTotalPayable <= 0 && paymentMethod !== 'COD') {
      alert('Cart total must be greater than zero.');
      return;
    }

    const resolvedBucketId =
      rawBucketData?.buy_now_bucket?._id ||
      rawBucketData?.buy_now_bucket?.bucket_id ||
      rawBucketData?.bucket_id ||
      rawBucketData?._id;

    if (!resolvedBucketId) {
      alert('Bucket reference not found. Please refresh and try again.');
      return;
    }

    setIsProcessingPayment(true);

    try {
      const cleanProjectId =
        typeof deliveryAddress?.project_id === 'object'
          ? deliveryAddress?.project_id?._id
          : deliveryAddress?.project_id || null;

      const payload = {
        amount: Math.round(selectedTotalPayable),
        customerId: customerId,
        hubId: customer?.hubId || 'default',
        projectId: cleanProjectId,
        bucketId: resolvedBucketId,
        paymentMethod: paymentMethod === 'COD' ? 'COD' : 'ONLINE',
        selected_delivery_type: selectedDeliveryOption?.type || selectedDeliveryType,
        delivery_type: selectedDeliveryOption?.type || selectedDeliveryType,
        pricing_token: rawBucketData?.pricing_token,
      };

      const res = await initiatePaymentMutation.mutateAsync(payload);
      const resData = res?.data?.data || res?.data || res;

      if (paymentMethod === 'COD' || resData?.payment_mode === 'COD') {
        const orderId = resData?.order_id || resData?.orderId;
        setPaymentStatus({
          type: 'cod',
          navOrderId: orderId,
          message: 'Your order is confirmed! Pay in cash upon delivery.',
        });
        queryClient.setQueryData(['bucketSummary', customerId], (prev) => ({
          ...prev,
          buy_now_bucket: {
            ...(prev?.buy_now_bucket || {}),
            items_count: 0,
            items: [],
            bill_summary: { total_payable: 0, sub_total: 0 },
          },
        }));
        queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerId] });
        queryClient.invalidateQueries({ queryKey: ['bucketDetails', customerId] });
        queryClient.invalidateQueries({ queryKey: ['customerOrders', customerId] });
        refetchBucketData();
        setIsProcessingPayment(false);
        return;
      }

      const targetGateway = (resData?.gateway || resData?.provider || 'CASHFREE').toUpperCase();
      const currentOrderId = resData?.order_id || resData?.orderId;

      if (targetGateway === 'CASHFREE') {
        const { payment_session_id } = resData;
        if (!payment_session_id) throw new Error('Invalid Cashfree session received.');

        const cashfree = await load({ mode: 'sandbox' });

        cashfree
          .checkout({
            paymentSessionId: payment_session_id,
            redirectTarget: '_modal',
          })
          .then((result) => {
            if (result.error) {
              setPaymentStatus({ type: 'failed', message: result.error.message || 'Payment cancelled by user.' });
              setIsProcessingPayment(false);
            }
            if (result.paymentDetails) {
              verifyBackendPaymentStatus(currentOrderId);
            }
          });
      } else if (targetGateway === 'PAYTM') {
        const { txnToken, mid, amount: serverAmount } = resData;
        if (!txnToken || !mid) throw new Error('Incomplete Paytm credentials returned.');

        const scriptSrc = `https://securegw-stage.paytm.in/merchantpgpui/checkoutjs/merchants/${mid}.js`;
        const script = document.createElement('script');
        script.src = scriptSrc;
        script.crossOrigin = 'anonymous';
        script.onload = () => {
          const config = {
            root: '',
            flow: 'DEFAULT',
            data: {
              orderId: currentOrderId,
              token: txnToken,
              tokenType: 'TXN_TOKEN',
              amount: serverAmount || selectedTotalPayable,
            },
            handler: {
              transactionStatus: function () {
                window.Paytm.CheckoutJS.close();
                verifyBackendPaymentStatus(currentOrderId);
              },
              notifyMerchant: function (eventName) {
                if (eventName === 'APP_CLOSED') {
                  setPaymentStatus({ type: 'failed', message: 'You closed the payment popup.' });
                  setIsProcessingPayment(false);
                }
              },
            },
          };
          window.Paytm.CheckoutJS.init(config).then(function () {
            window.Paytm.CheckoutJS.invoke();
          });
        };
        document.body.appendChild(script);
      } else {
        throw new Error(`Unsupported payment gateway: ${targetGateway}`);
      }
    } catch (err) {
      console.error('Instant checkout error:', err);
      setPaymentStatus({
        type: 'failed',
        message: err?.response?.data?.message || err?.message || 'Unable to complete checkout.',
      });
      setIsProcessingPayment(false);
    }
  };

  const getActionableAdvice = (reasonCode) => {
  switch (reasonCode) {
    case 'LOAD_EXCEEDS_VEHICLE_CAPACITY':
      return 'Action: Try reducing the item quantity or splitting your bucket into multiple orders.';
    case 'OUT_OF_SERVICE_RADIUS':
      return 'Action: Please select a different delivery address.';
    case 'OUT_OF_STOCK':
      return 'Action: Please remove out-of-stock items from your bucket.';
    default:
      return 'Action: Please review your bucket or contact support for assistance.';
  }
};

  const handleOrderNavigation = async () => {
    if (paymentStatus.type === 'failed' || paymentStatus.type === 'cancelled') {
      setPaymentStatus(null);
      return;
    }
    const targetTransactionId = paymentStatus.navOrderId || paymentStatus.displayOrderId;

    if (!targetTransactionId) {
      setPaymentStatus(null);
      onClose();
      return;
    }

    setIsNavigating(true);
    try {
      const response = await getOrderMutation.mutateAsync({
        transaction_order_id: targetTransactionId,
      });
      const realOrderId = response?.data?.order_id || response?.order_id;

      setPaymentStatus(null);
      onClose();

      if (realOrderId) {
        navigate(`/consumer/account/orders/${realOrderId}`);
      } else {
        navigate(`/consumer/account/orders`);
      }
    } catch (err) {
      console.error('Failed to map transaction to order:', err);
      setPaymentStatus(null);
      onClose();
      navigate(`/consumer/account/orders`);
    } finally {
      setIsNavigating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="bb-bucket-overlay" onClick={onClose}>
        <style>{`
          .bb-bucket-overlay {
            position: fixed;
            inset: 0;
            background-color: rgba(15, 23, 42, 0.55);
            z-index: 99999;
            display: flex;
            justify-content: flex-end;
            backdrop-filter: blur(3px);
            overscroll-behavior: contain;
          }

          .bb-bucket-drawer-shell {
            width: 100%;
            max-width: 460px;
            height: 100vh;
            height: 100dvh;
            background-color: #FAF9F6;
            box-shadow: -8px 0 32px rgba(0, 0, 0, 0.16);
            display: flex;
            flex-direction: column;
            position: relative;
            animation: slideInDrawer 0.24s cubic-bezier(0.16, 1, 0.3, 1);
            box-sizing: border-box;
          }

          @keyframes slideInDrawer {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }

          .bb-bucket-header {
            padding: 16px 20px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #E2E8F0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-shrink: 0;
          }

          .bb-bucket-body-scroll {
            flex: 1;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            padding: 16px 18px 24px 18px;
            display: flex;
            flex-direction: column;
            gap: 14px;
            overscroll-behavior: contain;
          }

          .bb-bucket-footer {
            background-color: #FFFFFF;
            border-top: 1px solid #E2E8F0;
            padding: 14px 18px;
            padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px));
            display: flex;
            flex-direction: column;
            gap: 10px;
            flex-shrink: 0;
            box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.05);
          }

          @media (max-width: 640px) {
            .bb-bucket-drawer-shell {
              max-width: 100vw;
              width: 100vw;
            }

            .bb-bucket-header {
              padding: 14px 16px;
            }

            .bb-bucket-body-scroll {
              padding: 12px 14px 20px 14px;
              gap: 12px;
            }

            .bb-bucket-footer {
              padding: 12px 14px;
              padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
            }
          }
          .bb-tooltip-container {
            position: relative;
            display: inline-flex;
            align-items: center;
            cursor: help;
          }
          .bb-tooltip-text {
            visibility: hidden;
            width: 250px;
            background-color: #1E293B;
            color: #F8FAFC;
            text-align: left;
            border-radius: 6px;
            padding: 8px 12px;
            position: absolute;
            z-index: 999;
            bottom: 125%;
            left: 50%;
            transform: translateX(-50%);
            font-size: 11px;
            line-height: 1.4;
            box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
          }
          .bb-tooltip-text::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: #1E293B transparent transparent transparent;
          }
          .bb-tooltip-container:hover .bb-tooltip-text {
            visibility: visible;
            opacity: 1;
          }
        `}</style>

        <div className="bb-bucket-drawer-shell" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bb-bucket-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h3 style={styles.headerTitle}>Bucket Items</h3>
              <span style={styles.itemCountBadge}>({displayItems.length})</span>
            </div>
            <button style={styles.closeBtn} onClick={onClose} type="button">
              <img src={X} alt="close" style={{ width: 14, height: 14 }} />
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="bb-bucket-body-scroll">
            {/* 1. Address Card */}
            {isAuthenticated && displayItems.length > 0 && (
              <div style={styles.addressCard}>
                <div style={styles.addressCardHeader}>
                  <span style={styles.addressTitle}>{addressDisplayName}</span>
                  <button
                    style={styles.changeAddressBtn}
                    onClick={() => setIsAddressModalOpen(true)}
                    type="button"
                  >
                    Change
                  </button>
                </div>
                <div style={styles.addressContent}>
                  {isAddressLoading ? (
                    <span style={{ fontSize: 12, color: '#707B8C' }}>Locating site address...</span>
                  ) : (
                    <p style={styles.addressText}>{formattedAddress}</p>
                  )}
                </div>
              </div>
            )}


          {/*   {isAuthenticated && !deliveryServiceable && displayItems.length > 0 && (
                <div style={styles.unserviceableAlert}>
                  
                
                  <div style={styles.alertIconWrapper}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                  </div>

                 
                  <div style={styles.alertContent}>
                    <strong style={styles.alertTitle}>Delivery Unavailable</strong>
                    <p style={styles.alertMessage}>
                      {deliveryFailureMessage || 'This order cannot be delivered to your location.'}
                    </p>
                    
                   
                    {deliveryFailureReason && (
                      <p style={styles.alertActionText}>
                        {getActionableAdvice(deliveryFailureReason)}
                      </p>
                    )}
                  </div>
                  
                </div>
              )} */}

              {isAuthenticated && displayItems.length > 0 && (
                !deliveryAddress ? (
                  <div style={{ ...styles.unserviceableAlert, backgroundColor: '#FFF4E5', borderColor: '#FFE0B2' }}>
                    
                    {/* Info Alert Icon for Missing Address */}
                    <div style={styles.alertIconWrapper}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ED6C02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                      </svg>
                    </div>

                    <div style={styles.alertContent}>
                      <strong style={{ ...styles.alertTitle, color: '#ED6C02' }}>Delivery Address Required</strong>
                      <p style={styles.alertMessage}>
                        Please select or add a delivery site address to calculate delivery options and check serviceability.
                      </p>
                      <p 
                        style={{ ...styles.alertActionText, cursor: 'pointer' }}
                        onClick={() => setIsAddressModalOpen(true)}
                      >
                        Action: Click here to select an address.
                      </p>
                    </div>
                    
                  </div>
                ) : !deliveryServiceable ? (
                  <div style={styles.unserviceableAlert}>
                    
                    {/* Error Alert Icon for Unserviceable Address */}
                    <div style={styles.alertIconWrapper}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                      </svg>
                    </div>

                    {/* Alert Text Content */}
                    <div style={styles.alertContent}>
                      <strong style={styles.alertTitle}>Delivery Unavailable</strong>
                      <p style={styles.alertMessage}>
                        {deliveryFailureMessage || 'This order cannot be delivered to your location.'}
                      </p>
                      
                      {/* Actionable Advice based on the specific payload reason */}
                      {deliveryFailureReason && (
                        <p style={styles.alertActionText}>
                          {getActionableAdvice(deliveryFailureReason)}
                        </p>
                      )}
                    </div>
                    
                  </div>
                ) : null
              )}

            {/* Out of Stock Alert Banner */}
            {hasOutOfStockItems && (
              <div style={styles.outOfStockAlertBanner}>
                <strong style={{ display: 'block', fontSize: 12.5, color: '#DC2626' }}>
                  Items out of stock in your bucket
                </strong>
                <span style={{ fontSize: 11.5, color: '#EF4444' }}>
                  Please remove out of stock items to proceed with placing your order.
                </span>
              </div>
            )}

            {/* 2. Delivery Options */}
            {isAuthenticated && deliveryOptions.length > 0 && mfcItems.length > 0 && (
              <div style={styles.deliveryCard}>
                <div style={styles.deliveryCardHeader}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: '700', color: '#2B2A29' }}>
                      Delivery options
                    </h4>
                    <span style={{ fontSize: 11, color: '#707B8C' }}>
                      Choose a convenient time for your order
                    </span>
                  </div>
                  <img src={Truck} alt="delivery" style={{ width: 20, height: 20 }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {deliveryOptions.map((opt) => {
                    const isSelected = selectedDeliveryOption?.type === opt.type;
                    const isFast = opt.type === 'FAST';
                    const charge = Number(opt.deliveryCharge) || 0;

                    return (
                      <div
                        key={opt.type}
                        style={{
                          ...styles.deliveryOptionItem,
                          ...(isSelected ? styles.deliveryOptionActive : {}),
                        }}
                        onClick={() => setSelectedDeliveryType(opt.type)}
                      >
                        <div style={styles.radioCircle}>
                          {isSelected && <div style={styles.radioDot} />}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, fontWeight: '700', color: '#2B2A29' }}>
                                {isFast ? 'Fast delivery' : 'Scheduled delivery'}
                              </span>
                              {isFast && (
                                <div style={styles.fastEtaBadge}>
                                  <img src={Lightning} alt="fast" style={{ width: 11, height: 11 }} />
                                  <span>{rawBucketData?.delivery_eta?.minMinutes || 30} - {rawBucketData?.delivery_eta?.maxMinutes || 90} mins</span>
                                </div>
                              )}
                            </div>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: '700',
                                color: charge > 0 ? '#2B2A29' : '#1E8449',
                                flexShrink: 0,
                              }}
                            >
                              {charge > 0 ? `₹${charge.toFixed(2)}` : 'Free'}
                            </span>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            {!isFast && <img src={Clock} alt="clock" style={{ width: 12, height: 12, opacity: 0.6 }} />}
                            <span style={{ fontSize: 11, color: '#707B8C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {opt.displayText || opt.slot?.label || 'Direct site delivery'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 3. Items List Container */}
            <div style={styles.itemsWrapper}>
          
         {/* NEW: Show a loading state if API is fetching */}
              {isLoadingItems ? (
                <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                   <div style={{ 
                     display: 'inline-block', 
                     width: 24, 
                     height: 24, 
                     border: '3px solid #E2E8F0', 
                     borderTopColor: '#E9001E', 
                     borderRadius: '50%', 
                     animation: 'spin 1s linear infinite' 
                   }} />
                   <p style={{ margin: '12px 0 0 0', fontSize: 13, color: '#707B8C' }}>
                     Loading your bucket...
                   </p>
                   <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>

              ) : displayItems.length === 0 ? (
                
                <div style={styles.emptyCartBox}>
                  <img src={bucket} alt="Bucket" style={styles.bucketImg} />
                  <h4 style={{ margin: '8px 0 2px 0', color: '#2B2A29' }}>Your bucket is empty</h4>
                  <p style={{ margin: '0 0 16px 0', fontSize: 12, color: '#707B8C' }}>Let's start building!</p>
                  <button 
                    style={styles.exploreBtn}
                    onClick={() => {
                      navigate('/consumer/search'); 
                      onClose(); 
                    }}
                    type="button"
                  >
                    Explore Products
                  </button>
                </div>
                
              ) : (
                <>
                  {/* Group A: Standard Items */}
                  {mfcItems.length > 0 && (
                    <>
                      <div style={styles.itemsCountHeader}>
                        <span> Items ({mfcItems.length})</span>
                      </div>
                      {mfcItems.map((item) => {
                        const pid = String(item.item_id || item.product_id || item._id);
                        return (
                          <BucketItemCard
                            key={pid}
                            item={item}
                            customerId={customerId}
                            isSavedItem={savedItemsSet.has(pid)}
                            onUpdateQty={handleUpdateQty}
                            onRemove={handleRemoveItem}
                            onToggleSave={handleToggleSave}
                          />
                        );
                      })}
                    </>
                  )}

                  {/* Group B: Bulk Items */}
                  {supplierItems.length > 0 && (
                    <>
                      <div
                        style={{
                          ...styles.itemsCountHeader,
                          backgroundColor: '#F8FAFC',
                          borderTop: mfcItems.length > 0 ? '1px solid #CBD5E1' : 'none',
                        }}
                      >
                        <span> Items ({supplierItems.length})</span>
                      </div>
                      {supplierItems.map((item) => {
                        const pid = String(item.item_id || item.product_id || item._id);
                        return (
                          <BucketItemCard
                            key={pid}
                            item={item}
                            customerId={customerId}
                            isSavedItem={savedItemsSet.has(pid)}
                            onUpdateQty={handleUpdateQty}
                            onRemove={handleRemoveItem}
                            onToggleSave={handleToggleSave}
                            isSupplierDelivered={true}
                          />
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>

            {displayItems.length > 0 && (
              <>
                {/* 4. Bill Summary Card */}
                <div style={styles.summaryCard}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: '700', color: '#2B2A29' }}>
                    Bill Summary
                  </h4>

                  <div style={styles.summaryRow}>
                    <span>Item Total</span>
                    <span style={{ fontWeight: '600' }}>
                      ₹{(isAuthenticated ? Number(billSummary?.sub_total || 0) : guestSubtotal).toFixed(2)}
                    </span>
                  </div>

                  {isAuthenticated ? (
                    <>
                      <div style={styles.summaryRow}>
                        <span>Tax</span>
                        <span style={{ fontWeight: '600' }}>₹{Number(billSummary?.total_tax || 0).toFixed(2)}</span>
                      </div>
                      <div style={styles.summaryRow}>
                        <span>Delivery charge</span>
                        <span
                          style={{
                            fontWeight: '600',
                            color: selectedDeliveryCharge > 0 ? '#2B2A29' : '#1E8449',
                          }}
                        >
                          {selectedDeliveryCharge > 0 ? `₹${selectedDeliveryCharge.toFixed(2)}` : 'Free'}
                        </span>
                      </div>

                      {/* --- NEW: Brick Points Redemption --- */}
                      {Number(billSummary?.brick_points_redeemable) > 0 && (
                        <div style={{ ...styles.summaryRow, alignItems: 'center', margin: '4px 0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>Brick Points </span>
                            <span style={{ fontSize: 11, color: '#16A34A', fontWeight: '700', backgroundColor: '#DCFCE7', padding: '2px 6px', borderRadius: 4, display: 'flex', alignItems: 'center' }}>
                              <img src={BrickPointImage} alt="Brick Point" style={{ width: 14, height: 14, marginRight: 4 }} />
                              {Number(billSummary.brick_points_redeemable)} Available
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {billSummary?.is_redeemable_brick_points_applied && (
                              <span style={{ color: '#16A34A', fontWeight: '700' }}>
                                -₹{Number(billSummary.brick_points_redeemable).toFixed(2)}
                              </span>
                            )}
                            {/* <div className="bb-tooltip-container">
                              <input
                                type="checkbox"
                                checked={billSummary?.is_redeemable_brick_points_applied || false}
                                //onChange={handleBrickPointsToggle}
                                disabled={updateRedeemableBrickPointsMutation.isLoading}
                                style={{ 
                                  width: 18, 
                                  height: 18, 
                                  cursor: updateRedeemableBrickPointsMutation.isLoading ? 'not-allowed' : 'pointer', 
                                  accentColor: '#16A34A', // Green tick when checked
                                  opacity: updateRedeemableBrickPointsMutation.isLoading ? 0.5 : 1
                                }}
                              />
                               <div className="bb-tooltip-text" style={{ bottom: '150%' }}>
                                {billSummary?.is_redeemable_brick_points_applied ? 'Remove Brick Points' : 'Apply Brick Points (1 Point = ₹1)'}
                              </div> 
                            </div> */}
                          </div>
                        </div>
                      )}


                      {billSummary?.discount_applied > 0 && (
                        <div style={styles.summaryRow}>
                          <span>Item discount</span>
                          <span style={{ color: '#1E8449', fontWeight: '600' }}>
                            -₹{Number(billSummary.discount_applied).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {billSummary?.brick_points_redeemed > 0 && (
                        <div style={styles.summaryRow}>
                          <span>Brick Points Redeemed</span>
                          <span style={{ color: '#1E8449', fontWeight: '600' }}>
                            -₹{Number(billSummary.brick_points_redeemed).toFixed(2)}
                          </span>
                        </div>
                      )} 

                      {billSummary?.wallet_balance > 0 && (
                        <div style={styles.summaryRow}>
                          <span>Wallet Applied</span>
                          <span style={{ color: '#1E8449', fontWeight: '600' }}>
                            -₹{Number(billSummary.wallet_balance).toFixed(2)}
                          </span>
                        </div>
                      )}
                      
                     {/*  {billSummary?.is_unloading_applied && (
                        <div style={styles.summaryRow}>
                          <span>Unloading charge</span>
                          <span style={{ fontWeight: '600', color: '#2B2A29' }}>
                            ₹{Number(billSummary.unloading_charges || 0).toFixed(2)}
                          </span>
                        </div>
                      )} */}

                      {billSummary?.is_unloading_applied && (
                      <div style={styles.summaryRow}>
                        <span>Unloading charge</span>
                        <span 
                          style={{ 
                            fontWeight: '600', 
                            color: Number(billSummary.unloading_charges) > 0 ? '#2B2A29' : '#388E3C' 
                          }}
                        >
                          {Number(billSummary.unloading_charges) > 0
                            ? `₹${Number(billSummary.unloading_charges).toFixed(2)}`
                            : 'Free'}
                        </span>
                      </div>
                    )}

                      <div style={styles.receiptDivider} />
                      <div style={{ ...styles.summaryRow, fontSize: 15 }}>
                        <strong>Total Payable</strong>
                        <strong>₹{Number(selectedTotalPayable).toFixed(2)}</strong>
                      </div>

                      {billSummary?.discount_applied > 0 && (
                        <div style={styles.savingsPill}>
                          You saved ₹{Number(billSummary.discount_applied).toFixed(2)} on this order
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={styles.unauthNoticeBox}>
                      <span>
                        Log in to see your exact total. Applicable charges and discounts will be calculated based on your delivery site.
                      </span>
                    </div>
                  )}
                </div>

                {isAuthenticated && billSummary?.is_show_unloading_charges && (
                  <div style={styles.unloadingCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <strong style={{ fontSize: 13, color: '#2B2A29' }}>Add Unloading Service</strong>
                          <div className="bb-tooltip-container">
                            <span style={styles.infoIconCircle}>i</span>
                            <div className="bb-tooltip-text">
                              Unloading is at your place from the vehicle only and not to any floor (ground level only) with a distance of up to 400m. More than that, we do not unload.
                            </div>
                          </div>
                        </div>
                        <span style={{ fontSize: 11, color: '#707B8C', display: 'block', marginTop: 2 }}>
                          Service by Brick Bucket team at delivery
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: '700', color: '#2B2A29' }}>
                          ₹{Number(billSummary?.unloading_charges || 0).toFixed(2)}
                        </span>
                        <input
                          type="checkbox"
                          checked={billSummary?.is_unloading_applied || false}
                          onChange={handleUnloadingToggle}
                          style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#E9001E' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* 5. Offers */}
                {isAuthenticated ? (
                  rawBucketData?.discounts && rawBucketData.discounts.discount > 0 ? (
                    <div style={styles.discountCardApplied}>
                      <div style={styles.discountIconBoxGreen}>
                        <img src={SealPercent} alt="%" style={{ width: 18, height: 18 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: 13, color: '#16A34A', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rawBucketData.discounts.discount_title || 'Applied Offer'}
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          ₹{rawBucketData.discounts.discount?.toFixed(2)} applied
                        </span>
                      </div>
                      <button
                        style={styles.removeDiscountBtn}
                        onClick={() => handleRemoveDiscount(rawBucketData.discounts.rule_code)}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div style={styles.discountCard} onClick={handleOpenDiscounts}>
                      <div style={styles.discountIconBox}>
                        <img src={SealPercent} alt="offer" style={{ width: 18, height: 18 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                          Apply Offer
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          Tap to view all available discount offers.
                        </span>
                      </div>
                      <img src={ArrowRight} alt="arrow" style={{ width: 12, height: 12 }} />
                    </div>
                  )
                ) : (
                  <div
                    style={styles.lockedCard}
                    onClick={() => {
                      onClose();
                      openLoginModal();
                    }}
                  >
                    <div style={styles.lockedIconBox}>
                      <span style={{ fontSize: 14 }}>🔒</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                        Coupons & Offers Locked
                      </strong>
                      <span style={{ fontSize: 11, color: '#707B8C' }}>
                        Log in to claim welcome discounts and promotional savings
                      </span>
                    </div>
                    <span style={styles.unlockActionText}>Unlock</span>
                  </div>
                )}

              
                {/* Brick Points Module */}
{isAuthenticated && brickPointsEarned > 0 && (
  <div style={styles.brickPointsCard}>
    {brickPointsEligible ? (
      <div style={styles.bpRow}>
        <div style={styles.bpIconCircle}>
          <img src={BrickPointImage} alt="Brick Points" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <strong
            style={{
              fontSize: 13,
              color: '#2B2A29',
              display: 'block',
            }}
          >
            Earn {brickPointsEarned} Brick Points{' '}
            {customer_status === 3
              ? 'on this order'
              : !PointsLocked
              ? '⏳ Pending Approval'
              : '🔒 Locked'}
          </strong>

          <span
            style={{
              fontSize: 11,
              color: '#707B8C',
            }}
          >
            {customer_status === 3
              ? '1 Brick point = 1₹ discount on future purchases'
              : !PointsLocked
              ? 'Brick Points are unlocked and waiting for final approval.'
              : '1 Brick Point = ₹1 discount. Unlock to start earning.'}
          </span>
        </div>

        {/* Right-Side Dynamic Action Area */}
        {PointsLocked ? (
          <button
            style={styles.unlockPointsBtn}
            onClick={() => setIsPointsModalOpen(true)}
          >
            Unlock
          </button>
        ) : (
          customer_status === 3 && recommendationEligible && (
            <>
            <button
              style={styles.shareEarnBtnSmall}
              onClick={handleShareAndEarn}
              title="Recommend BrickBucket to others and earn additional Brick Points"
            >
              {/* <span style={{ marginRight: 4, fontSize: 12 }}>🎁</span>  */}
              Share & Earn
            </button>
        {/*
          <button
      style={styles.shareEarnBtnSmall}
      //onClick={handleShareAndEarn}
      title="Recommend BrickBucket to others and earn additional Brick Points"
    >
      <svg 
        width="14" 
        height="14" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ marginRight: 6 }}
      >
        <circle cx="18" cy="5" r="3"></circle>
        <circle cx="6" cy="12" r="3"></circle>
        <circle cx="18" cy="19" r="3"></circle>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
      </svg>
      &  Earn
    </button> 
    */}
    </>
          )
        )}
      </div>
    ) : null}
  </div>
)}

                {/* 6. Wallet */}
                {isAuthenticated && availableWalletBalance > 0 && (
                  <div style={styles.walletCard}>
                    <div style={styles.walletIconBox}>
                      <img src={WalletIcon} alt="wallet" style={{ width: 18, height: 18 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                        Use Wallet Balance
                      </strong>
                      <span style={{ fontSize: 11, color: '#707B8C' }}>
                        Available: ₹{availableWalletBalance}
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={useWallet}
                      onChange={handleWalletToggle}
                      style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#E9001E' }}
                    />
                  </div>
                )}

                {/* 7. Payment Options */}
                {isAuthenticated && (
                  <div style={styles.paymentCard}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: 14, fontWeight: '700', color: '#2B2A29' }}>
                      Payment method
                    </h4>

                    <div
                      style={{
                        ...styles.paymentOption,
                        ...(paymentMethod === 'ONLINE' ? styles.paymentOptionActive : {}),
                      }}
                      onClick={() => setPaymentMethod('ONLINE')}
                    >
                      <input
                        type="radio"
                        name="payMethod"
                        checked={paymentMethod === 'ONLINE'}
                        onChange={() => setPaymentMethod('ONLINE')}
                        style={{ accentColor: '#E9001E' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                          Pay online
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          UPI, Cards, Net Banking & Wallets powered by Cashfree
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        ...styles.paymentOption,
                        ...(paymentMethod === 'COD' ? styles.paymentOptionActive : {}),
                        ...(selectedTotalPayable > 10000 ? { opacity: 0.6 } : {}),
                      }}
                      onClick={() => {
                        if (selectedTotalPayable <= 10000) setPaymentMethod('COD');
                      }}
                    >
                      <input
                        type="radio"
                        name="payMethod"
                        checked={paymentMethod === 'COD'}
                        disabled={selectedTotalPayable > 10000}
                        onChange={() => setPaymentMethod('COD')}
                        style={{ accentColor: '#E9001E' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                          Cash on Delivery
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          {selectedTotalPayable > 10000
                            ? 'COD is only available for orders under ₹10,000'
                            : 'Pay in cash when materials are unloaded at site'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 8. Trust Banner */}
                {isAuthenticated && (
                  <div style={styles.trustBanner}>
                    <img src={ShieldCheck} alt="shield" style={{ width: 16, height: 16 }} />
                    <span>100% Secure Payments • GST Invoice Emailed</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sticky Drawer Footer */}
          {displayItems.length > 0 && (
            <div className="bb-bucket-footer">
              {isAuthenticated && isBulkEligible && (
                <div
                  style={{
                    ...styles.bulkStrip,
                    backgroundColor: isOverLimit ? '#FFFBEB' : '#EFF6FF',
                    borderColor: isOverLimit ? '#FDE68A' : '#BFDBFE',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: '700',
                      color: isOverLimit ? '#92400E' : '#1E40AF',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {isOverLimit
                      ? 'Request for Quote required for orders exceeding ₹1 Lakh'
                      : 'Order eligible for Request for Quote (RFQ)'}
                  </span>
                  <button
                    style={styles.bulkStripBtn}
                    onClick={() => {
                      onClose();
                      navigate('/consumer/rfq');
                    }}
                    type="button"
                  >
                    Request Quote
                  </button>
                </div>
              )}

              <div style={styles.footerMainRow}>
                <div style={{ flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: '#707B8C', display: 'block' }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: '800', color: '#2B2A29' }}>
                    ₹
                    {(isAuthenticated
                      ? Number(selectedTotalPayable)
                      : guestSubtotal
                    ).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>

                {isAuthenticated ? (
                  <button
                    style={{
                      ...styles.payNowBtn,
                      opacity: (isProcessingPayment || !deliveryServiceable) ? 0.7 : 1,
                      cursor: (!deliveryServiceable) ? 'not-allowed' : 'pointer',
                      backgroundColor: !deliveryServiceable ? '#cccccc' : styles.payNowBtn.backgroundColor,
                    }}
                    disabled={isProcessingPayment || !deliveryServiceable}
                    onClick={handleCheckout}
                    type="button"
                  >
                    {!deliveryServiceable 
                    ? 'Delivery Unavailable' 
                    : isProcessingPayment
                    ? 'Processing...'
                    : paymentMethod === 'COD'
                    ? 'Place COD Order'
                    : 'Pay Now'}
                  </button>
                ) : (
                  <button
                    style={styles.payNowBtn}
                    onClick={() => {
                      onClose();
                      openLoginModal();
                    }}
                    type="button"
                  >
                    Login to Proceed
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Payment Status Overlay */}
          {paymentStatus && (
            <div style={styles.statusModalOverlay}>
              <div style={styles.statusCard}>
                <div
                  style={{
                    ...styles.statusIconCircle,
                    backgroundColor: paymentStatus.type === 'failed' ? '#FEE2E2' : '#DCFCE7',
                  }}
                >
                  <img
                    src={paymentStatus.type === 'failed' ? X : ShieldCheck}
                    alt="status"
                    style={{ width: 28, height: 28 }}
                  />
                </div>
                <h3 style={{ margin: '10px 0 6px 0', fontSize: 18, color: '#2B2A29' }}>
                  {paymentStatus.type === 'failed' ? 'Payment Failed' : 'Order Placed!'}
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#636C78', textAlign: 'center' }}>
                  {paymentStatus.message}
                </p>
                {paymentStatus.displayOrderId && (
                  <span style={{ fontSize: 12, fontWeight: '700', color: '#2B2A29', marginBottom: 16 }}>
                    Order ID: {paymentStatus.displayOrderId}
                  </span>
                )}
                <button
                  style={{
                    ...styles.statusActionBtn,
                    opacity: isNavigating ? 0.7 : 1,
                  }}
                  disabled={isNavigating}
                  onClick={handleOrderNavigation}
                  type="button"
                >
                  {isNavigating
                    ? 'Loading Order...'
                    : paymentStatus.type === 'failed'
                    ? 'Retry Payment'
                    : 'View Order'}
                </button>
              </div>
            </div>
          )}

          {/* Discount Modal */}
          {isDiscountModalOpen && (
            <div style={styles.statusModalOverlay} onClick={() => setIsDiscountModalOpen(false)}>
              <div style={styles.couponModalBox} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h4 style={{ margin: 0, fontSize: 16, color: '#2B2A29' }}>Apply Offers</h4>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => setIsDiscountModalOpen(false)}
                    type="button"
                  >
                    ✕
                  </button>
                </div>

                {isEvaluatingDiscounts ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#707B8C' }}>
                    Evaluating active offers...
                  </div>
                ) : evaluatedDiscounts.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#707B8C' }}>
                    No coupons applicable for current items in bucket.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {evaluatedDiscounts.map((coupon, idx) => (
                      <div key={coupon.code || idx} style={styles.couponTile}>
                        <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                          <strong style={{ fontSize: 13, color: '#E9001E' }}>{coupon.code}</strong>
                          <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#2B2A29' }}>
                            {coupon.title || coupon.sub_text}
                          </p>
                        </div>
                        <button
                          style={styles.applyCouponBtn}
                          onClick={() => handleApplyDiscount(coupon)}
                          type="button"
                        >
                          Apply
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Brick Points Professional Verification Modal */}
          {isPointsModalOpen && (
            <div style={styles.statusModalOverlay} onClick={() => setIsPointsModalOpen(false)}>
              <div style={{ ...styles.couponModalBox, padding: 24 }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 18, fontWeight: '800', color: '#1E293B' }}>
                      Unlock Brick Points
                    </h4>
                    <span style={{ fontSize: 13, color: '#64748B', display: 'block', marginTop: 4 }}>
                      Share your professional details to activate your rewards!
                    </span>
                  </div>
                  <button
                    style={{
                      background: '#F1F5F9',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#64748B',
                    }}
                    onClick={() => setIsPointsModalOpen(false)}
                    type="button"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                <div
                  style={{
                    backgroundColor: '#FFFBEB',
                    border: '1px solid #FEF3C7',
                    borderRadius: 8,
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: 24 }}>🪙</span>
                  <span style={{ fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
                    <strong>1 Coin = ₹1 Discount</strong>
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: '600', marginBottom: 6, display: 'block', color: '#334155' }}>
                      Customer Name <span style={{ color: '#94A3B8', fontWeight: '400', fontSize: 11 }}>(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter your full name"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        outline: 'none',
                        backgroundColor: '#F8FAFC',
                        fontSize: 14,
                        color: '#0F172A',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 13, fontWeight: '600', marginBottom: 6, display: 'block', color: '#334155' }}>
                      Choose your profession <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <select
                      value={profType}
                      onChange={(e) => setProfType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        outline: 'none',
                        backgroundColor: '#F8FAFC',
                        fontSize: 14,
                        color: '#0F172A',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="" disabled>Select your profession</option>
                      <option value="Pro Builder">Professional Builder</option>
                      <option value="Contractor">Contractor</option>
                      <option value="Supervisor">Supervisor</option>
                      <option value="Architect">Architect</option>
                      <option value="Skilled Professional">Skilled Professional</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: 13, fontWeight: '600', marginBottom: 6, display: 'block', color: '#334155' }}>
                      GSTIN <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={gstin}
                      onChange={(e) => setGstin(e.target.value)}
                      placeholder="Enter 15-digit GSTIN"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        outline: 'none',
                        backgroundColor: '#F8FAFC',
                        fontSize: 14,
                        color: '#0F172A',
                        textTransform: 'uppercase',
                      }}
                      maxLength={15}
                    />
                  </div>

                  <button
                    style={{
                      backgroundColor: profType && gstin ? '#E9001E' : '#F1F5F9',
                      color: profType && gstin ? '#FFFFFF' : '#94A3B8',
                      border: 'none',
                      borderRadius: 8,
                      padding: '14px',
                      fontSize: 14,
                      fontWeight: '700',
                      cursor: profType && gstin ? 'pointer' : 'not-allowed',
                      marginTop: 8,
                      transition: 'all 0.2s ease',
                      boxShadow: profType && gstin ? '0 4px 6px -1px rgba(233, 0, 30, 0.2)' : 'none',
                    }}
                    onClick={() => handleVerifyPointsProfile(customerName)}
                    disabled={!profType || !gstin}
                  >
                    Submit for Verification
                  </button>
                </div>
              </div>
            </div>
          )}

         {/* Share & Earn Modal */}
        {isShareModalOpen && (
          <div style={styles.statusModalOverlay} onClick={() => setIsShareModalOpen(false)}>
            <div style={{ ...styles.couponModalBox, padding: '24px' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: 18, fontWeight: '800', color: '#1E293B' }}>Share & Earn</h4>
                  <span style={{ fontSize: 11, fontWeight: '700', color: '#15803D', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: 4, display: 'inline-block', marginTop: 6 }}>
                    1 Point = ₹1 Discount
                  </span>
                </div>
                <button
                  style={{ background: '#F1F5F9', border: 'none', cursor: 'pointer', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}
                  onClick={() => setIsShareModalOpen(false)}
                >
                  ✕
                </button>
              </div>

              <p style={{ margin: '0 0 20px 0', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                Share this bucket with your clients. <strong style={{ color: '#1E293B' }}>When the customer places the order and completes the payment</strong> using your link, the points will be added directly to your wallet!
              </p>

              {/* Copy Box */}
              <div style={styles.copyBox}>
                <span style={styles.urlText}>{shareUrl}</span>
                <button style={styles.copyButton} onClick={copyToClipboard} title="Copy Link">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              </div>

              <div style={{ height: 1, backgroundColor: '#E2E8F0', marginBottom: 20 }}></div>

              {/* Social Share Grid */}
              <div style={styles.shareGrid}>
                <div style={styles.appIconWrapper} onClick={shareViaWhatsApp}>
                  <div style={{ ...styles.iconBackground, backgroundColor: '#E8F5E9' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#25D366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                    </svg>
                  </div>
                  <span style={styles.appName}>WhatsApp</span>
                </div>

                <div style={styles.appIconWrapper} onClick={shareViaTelegram}>
                  <div style={{ ...styles.iconBackground, backgroundColor: '#E1F0FA' }}>
                    <span style={{ color: '#0088cc', fontSize: 18, fontWeight: 'bold' }}>TG</span>
                  </div>
                  <span style={styles.appName}>Telegram</span>
                </div>

                <div style={styles.appIconWrapper} onClick={shareViaWebAPI}>
                  <div style={{ ...styles.iconBackground, backgroundColor: '#F1F5F9' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                  </div>
                  <span style={styles.appName}>More</span>
                </div>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>

      {isAuthenticated && (
        <DeliveryAddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          onAddNewAddress={(loc) => navigate('/consumer/account/delivery/new', { state: loc })}
        />
      )}
    </>
  );
}

const styles = {
  headerTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: '700',
    color: '#2B2A29',
  },
  itemCountBadge: {
    fontSize: 14,
    color: '#707B8C',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
  },
  addressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '74px',
    flexShrink: 0,
    overflow: 'hidden',
  },
  addressCardHeader: {
    backgroundColor: '#F8F6F2',
    padding: '10px 14px',
    borderBottom: '1px solid #CBD5E1',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addressTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#2B2A29',
  },
  changeAddressBtn: {
    background: 'none',
    border: 'none',
    color: '#E9001E',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
  },
  addressContent: {
    padding: '12px 14px',
  },
  addressText: {
    margin: 0,
    fontSize: 12,
    color: '#2B2A29',
    lineHeight: '16px',
  },
  unserviceableAlert: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    color: '#B91C1C',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    flexShrink: 0,
  },
  outOfStockAlertBanner: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flexShrink: 0,
  },
  deliveryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    flexShrink: 0,
  },
  deliveryCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  deliveryOptionItem: {
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  deliveryOptionActive: {
    borderColor: '#E9001E',
    backgroundColor: '#FFF5F5',
  },
  radioCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    border: '1.5px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#E9001E',
  },
  fastEtaBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#DCFCE7',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: '700',
    color: '#166534',
  },
  itemsWrapper: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'visible',
    flexShrink: 0,
  },
  itemsCountHeader: {
    padding: '10px 14px',
    backgroundColor: '#F8F6F2',
    borderBottom: '1px solid #CBD5E1',
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
    fontSize: 12,
    fontWeight: '700',
    color: '#2B2A29',
    display: 'block',
  },
  emptyCartBox: {
    padding: '36px 16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  bucketImg: {
    width: 32,
    height: 32,
    objectFit: 'contain',
    display: 'block',
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#2B2A29',
  },
  receiptDivider: {
    height: 1,
    backgroundColor: '#CBD5E1',
    margin: '4px 0',
  },
  savingsPill: {
    backgroundColor: '#DCFCE7',
    color: '#166534',
    fontSize: 11,
    fontWeight: '700',
    padding: '6px 10px',
    borderRadius: 6,
    textAlign: 'center',
    marginTop: 4,
  },
  unauthNoticeBox: {
    backgroundColor: '#FFF8F0',
    border: '1px solid #FFE4C8',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#9A5B18',
    fontSize: 12,
    lineHeight: '16px',
    marginTop: 8,
  },
  brickPointsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #FCD34D',
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
    overflow: 'hidden',
  },
  bpRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  bpIconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FEF3C7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    flexShrink: 0,
  },
  unlockPointsBtn: {
    background: 'none',
    border: '1px solid #E9001E',
    color: '#E9001E',
    fontSize: 11,
    fontWeight: '700',
    padding: '6px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    flexShrink: 0,
  },
  discountCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    flexShrink: 0,
  },
  discountCardApplied: {
    backgroundColor: '#F0FDF4',
    borderRadius: 10,
    border: '1px solid #86EFAC',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  discountIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#F5F3EF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  discountIconBoxGreen: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#DCFCE7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  removeDiscountBtn: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
    flexShrink: 0,
  },
  lockedCard: {
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    border: '1px dashed #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    flexShrink: 0,
  },
  lockedIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#EDF2F7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  unlockActionText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#E9001E',
    flexShrink: 0,
  },
  walletCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  walletIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#FEF3C7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  paymentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  paymentOption: {
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  paymentOptionActive: {
    borderColor: '#E9001E',
    backgroundColor: '#FFF5F5',
  },
  trustBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#16A34A',
    fontSize: 11,
    fontWeight: '600',
    padding: '8px 0',
    flexShrink: 0,
  },
  bulkStrip: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 6,
    padding: '6px 10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  bulkStripBtn: {
    background: 'none',
    border: 'none',
    color: '#0F4392',
    fontWeight: '700',
    fontSize: 11,
    cursor: 'pointer',
    flexShrink: 0,
  },
  footerMainRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  payNowBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: '700',
    cursor: 'pointer',
    flex: 1,
  },
  statusModalOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  statusCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 320,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statusIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  statusActionBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    width: '100%',
    fontWeight: '700',
    cursor: 'pointer',
  },
  couponModalBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: '100%',
    maxWidth: 360,
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  couponTile: {
    border: '1px dashed #CBD5E1',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  applyCouponBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 11,
    fontWeight: '700',
    cursor: 'pointer',
  },
  unloadingCard: {
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    border: '1px dashed #94A3B8',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
  },
  infoIconCircle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: '#94A3B8',
    color: '#FFFFFF',
    fontSize: 9,
    fontWeight: 'bold',
    fontStyle: 'italic',
  },
  exploreBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  unserviceableAlert: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FDEDED', // Soft red background for warning
    border: '1px solid #F4C7C7',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
  },
  alertIconWrapper: {
    marginRight: '12px',
    marginTop: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alertContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  alertTitle: {
    color: '#D32F2F', // Dark red for strong contrast
    fontSize: '14px',
    marginBottom: '4px',
  },
  alertMessage: {
    margin: '0 0 6px 0',
    fontSize: '13px',
    color: '#333333',
    lineHeight: '1.4',
  },
  alertActionText: {
    margin: '0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#1976D2', // Blue to indicate an actionable step/help
  },
  // Add to your styles object
  shareEarnBtnSmall: {
    backgroundColor: '#FFFBEB',
    border: '1px solid #FCD34D',
    color: '#B45309',
    fontSize: 11,
    fontWeight: '700',
    padding: '6px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s',
  },

  // --- Share Modal Styles ---
  copyBox: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 20,
  },
  urlText: {
    flex: 1,
    fontSize: 13,
    color: '#475569',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: 12,
    fontFamily: 'monospace'
  },
  copyButton: {
    backgroundColor: '#E2E8F0',
    border: 'none',
    padding: 8,
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareGrid: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  appIconWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
  },
  iconBackground: {
    width: 56,
    height: 56,
    borderRadius: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  appName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1C2833',
  },
};